import { ComponentProps, Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import AlloyAssigneePicker, {
  ConditionalRendering,
  FooterRendererProps,
  ItemWithGroup,
  ItemWithOptionalLabel,
} from '@adsk/alloy-assignee-picker';
import { DefaultItem } from '@adsk/alloy-react-dropdown';
import Tooltip from '@adsk/alloy-react-tooltip';
import Avatar from '@adsk/alloy-react-avatar';
import isEmpty from 'lodash/isEmpty';
import { User, Company } from '@adsk/alloy-provider';
import keyBy from 'lodash/keyBy';
import { ALL_USERS_SPECIAL_ID, EMPTY_ARRAY, EMPTY_OBJECT, HQUserStatus } from '../../../constants';
import { translate } from '../../../helpers';
import { Assignee, AssigneeType } from '../../../sdk';
import { useUsers } from '../../../providers/RFIsUsersProvider';
import AssigneeAvatar from '../../AssigneeAvatar';

const MAX_NUMBER_OF_SEEN_ROWS = 5;
const PIXELS_PER_MENU_ROW = 32;
const WRAPPER_STYLE = { '> div': { maxHeight: MAX_NUMBER_OF_SEEN_ROWS * PIXELS_PER_MENU_ROW } };
const AVATAR_STYLE = {
  marginRight: 8,
  fontWeight: 500,
};

const ADDITIONAL_USER_FIELDS: Array<keyof User> = ['imageUrl'];
const ADDITIONAL_COMPANY_FIELDS: Array<keyof Company> = ['imageUrl'];

type AlloyAssigneePickerProps = ComponentProps<typeof AlloyAssigneePicker>;
type FooterRenderer = (
  props: FooterRendererProps & { setIsOpen: Dispatch<SetStateAction<boolean>>; loading: boolean },
) => ConditionalRendering;

export type AssigneePickerProps = Omit<
  AlloyAssigneePickerProps,
  'onChange' | 'renderVirtualizedMenuFooter'
> & {
  assigneesOptions: Assignee[];
  selectedAssignees: Assignee[];
  disabledAssignees?: Assignee[];
  disabledAssigneesTooltip?: string;
  maxAssignees?: number;
  renderVirtualizedMenuFooter?: FooterRenderer;
  onChange?: (value: Assignee[]) => void;
  isOpen?: boolean;
  onClose?: () => void;
  hideDeletedUsers?: boolean;
};

const mapDropdownItemToSelectedAssignee = (item: ItemWithGroup): Assignee => ({
  type: item.group as AssigneeType,
  id: item.value,
});

const mapSelectedAssigneeToDropdownItem = (assignee: Assignee): ItemWithOptionalLabel => ({
  value: assignee.id,
});
export const AssigneePicker: FC<AssigneePickerProps> = ({
  assigneesOptions = EMPTY_OBJECT,
  selectedAssignees,
  disabledAssignees,
  maxAssignees,
  disabledAssigneesTooltip = translate('assigneePicker.answeredTooltip'),
  onChange,
  renderVirtualizedMenuFooter,
  selectPlaceholder,
  searchPlaceholder,
  isOpen = false,
  onClose,
  hideDeletedUsers = true,
  ...baseProps
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(isOpen);
  useEffect(() => {
    setIsMenuOpen(isOpen);
  }, [isOpen]);

  const assigneeOptionsDict = useMemo(
    () => keyBy(assigneesOptions as Assignee[], 'id'),
    [assigneesOptions],
  );

  const selectedAssigneesDict = useMemo(() => keyBy(selectedAssignees, 'id'), [selectedAssignees]);
  const optionsFilter = useMemo(
    (): AlloyAssigneePickerProps['optionsFilter'] => ({
      user: (value) => {
        if (hideDeletedUsers && value.status === HQUserStatus.DELETED) {
          return value.autodeskId in selectedAssigneesDict;
        }

        return (
          ALL_USERS_SPECIAL_ID in assigneeOptionsDict || value.autodeskId in assigneeOptionsDict
        );
      },
      company: (value) => value.memberGroupId in assigneeOptionsDict,
      role: (value) => value.memberGroupId in assigneeOptionsDict,
    }),
    [assigneeOptionsDict, hideDeletedUsers, selectedAssigneesDict],
  );

  const isRolesAndCompanies = useMemo(
    () =>
      Object.values(assigneeOptionsDict).some(
        (assignee) => assignee.type === AssigneeType.Company || assignee.type === AssigneeType.Role,
      ),
    [assigneeOptionsDict],
  );

  const isMultipleSelection = !maxAssignees || maxAssignees > 1;
  const dropdownProps: AlloyAssigneePickerProps['dropdownProps'] = useMemo(() => {
    const selectedItems = selectedAssignees?.map(mapSelectedAssigneeToDropdownItem) || EMPTY_ARRAY;
    const itemProps = isMultipleSelection
      ? { selectedItems }
      : { selectedItem: selectedItems[0] || null };

    return {
      ...itemProps,
      isOpen: isMenuOpen,
      onIsOpenChange: ({ isOpen: _isOpen = false }) => {
        if (_isOpen === false) {
          onClose?.();
        }

        setIsMenuOpen(_isOpen);
      },
    };
  }, [selectedAssignees, isMenuOpen, isMultipleSelection, onClose]);

  let maxAssigneesReached: boolean;
  const selectedAssigneesCount = selectedAssignees.length;
  if (isMultipleSelection) {
    maxAssigneesReached = !!maxAssignees && selectedAssigneesCount >= maxAssignees;
  } else {
    maxAssigneesReached = selectedAssigneesCount === 1;
  }

  const getItemDisabledInfo = (item: DefaultItem): { disabled: boolean; tooltip: string } => {
    if (disabledAssignees?.some(({ id }) => item.value === id)) {
      return {
        disabled: true,
        tooltip: disabledAssigneesTooltip,
      };
    }

    if (maxAssignees === 1 || !maxAssigneesReached) {
      return {
        disabled: false,
        tooltip: '',
      };
    }

    const isItemSelected = selectedAssignees.some((a) => a.id === item.value);
    return {
      disabled: !isItemSelected,
      tooltip: isItemSelected
        ? ''
        : translate('assigneePicker.maxMembersTooltip', { max: maxAssignees }),
    };
  };

  const handleOnChange = (value: ItemWithGroup | ItemWithGroup[]) => {
    if (isEmpty(value)) {
      onChange?.([]);
      return;
    }

    const assignees: Assignee[] = Array.isArray(value)
      ? value.map(mapDropdownItemToSelectedAssignee)
      : [mapDropdownItemToSelectedAssignee(value)];

    onChange?.(assignees);
  };

  const renderMenuItem: AlloyAssigneePickerProps['renderMenuItem'] = (props) => {
    const { item } = props;
    const { disabled, tooltip } = getItemDisabledInfo(item);
    // The item needs to remain referentially equal so we run over its disabled field
    item.disabled = disabled;
    const menuItem = AlloyAssigneePicker.defaultMenuItemRenderer({
      ...props,
      renderLeftComponent: () => (
        <AssigneeAvatar
          style={AVATAR_STYLE}
          size={Avatar.SIZES.MEDIUM_24}
          assignee={{ id: item.value, type: item.group as AssigneeType }}
        />
      ),
    });

    return tooltip ? (
      <Tooltip
        key={item.value}
        content={tooltip}
        placement={Tooltip.PLACEMENTS.BOTTOM}
        offset={[0, props.start + props.size]}
        hideArgs={EMPTY_OBJECT} // Prevents a bug that makes item tooltips not to appear
      >
        {menuItem}
      </Tooltip>
    ) : (
      menuItem
    );
  };

  const getSelectPlaceholder = (isMultiple: boolean) => {
    if (!isMultiple && !isRolesAndCompanies) {
      return translate('assigneePicker.selectPlaceholderSingle');
    }
    if (!isMultiple && isRolesAndCompanies) {
      return translate('assigneePicker.selectMemberRoleCompany');
    }
    if (maxAssignees && isRolesAndCompanies) {
      return translate('assigneePicker.selectMultiplePlaceholderRolesCompanies', { maxAssignees });
    }
    if (maxAssignees) {
      return translate('assigneePicker.selectMultiplePlaceholder', { maxAssignees });
    }
    if (isRolesAndCompanies) {
      return translate('assigneePicker.selectMemberRoleCompanyPlural');
    }
    return translate('assigneePicker.selectMembers');
  };

  const defaultSearchPlaceholder = isRolesAndCompanies
    ? translate('assigneePicker.searchMembersRolesCompanies')
    : translate('assigneePicker.searchPlaceholder');
  const rfiUsers = useUsers();

  return (
    <div css={WRAPPER_STYLE}>
      <AlloyAssigneePicker
        customUsers={rfiUsers}
        onChange={handleOnChange}
        dropdownProps={dropdownProps}
        searchable
        multiple={isMultipleSelection}
        optionsFilter={optionsFilter}
        optionsOrder={[AssigneeType.User, AssigneeType.Role, AssigneeType.Company]}
        additionalUserDataFields={ADDITIONAL_USER_FIELDS}
        additionalCompanyDataFields={ADDITIONAL_COMPANY_FIELDS}
        userOptionValue="autodeskId"
        roleOptionValue="memberGroupId"
        companyOptionValue="memberGroupId"
        renderMenuItem={renderMenuItem}
        userOptionLabel={translate('assigneePicker.userLabel')}
        showIcons
        selectPlaceholder={selectPlaceholder || getSelectPlaceholder(isMultipleSelection)}
        searchPlaceholder={searchPlaceholder || defaultSearchPlaceholder}
        clearFilterOnSelect
        {...(renderVirtualizedMenuFooter && {
          renderVirtualizedMenuFooter: (props) =>
            renderVirtualizedMenuFooter({
              ...props,
              setIsOpen: setIsMenuOpen,
              loading: rfiUsers.loading,
            }),
        })}
        {...baseProps}
      />
    </div>
  );
};
